import BreadCrumb from "Components/Common/BreadCrumb";
import React, { useEffect, useState } from "react";
import { Container, Row, Col } from "reactstrap";
import Widgets from "Components/Common/Widgets";
import Heading from "Components/Common/Heading";
import Button from "Components/Common/Button";
import Filter from "Components/Common/Filter";
import Table, { Column } from "Components/Common/Table";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  GetAllReports,
  UpdateReportStatus,
} from "endpoints/reports/index.action";
import { getAllCondos } from "endpoints/condo/index.action";
import Pills from "Components/Common/Pills";
import { formatDateTime } from "lib/utils/formatters";
import ViewReportDetailsModal from "Components/Modals/ViewReportDetailsModal";
import Spinner from "Components/Common/Spinner";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const Reports = () => {
  const { t } = useTranslation();
  const [accessData, setAccessData] = useState<any[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [search, setSearch] = useState<string>("");
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [lastSearch, setLastSearch] = useState<string>("");
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [paginationData, setPaginationData] = useState<{
    total: number;
    totalPages: number;
  }>({ total: 0, totalPages: 0 });
  const [adminCondoIds, setAdminCondoIds] = useState<string[]>([]);
  const [allFilteredTickets, setAllFilteredTickets] = useState<any[]>([]); // Store all filtered tickets for stats
  
  // Initialize stats with translated labels
  const [statsData, setStatsData] = useState<any[]>([
    {
      id: 1,
      label: t("reports.totalReports"),
      counter: "0",
      bgColor: "#007BFF14",
    },
    {
      id: 2,
      label: t("reports.openReports"),
      counter: "0",
      badge: "ri-arrow-up-s-fill",
      badgeColor: "success",
      bgColor: "#28C76F14",
    },
    {
      id: 3,
      label: t("reports.closedReports"),
      counter: "0",
      bgColor: "#FF3B3014",
    },
  ]);
  
  document.title = t("reports.title");

  // Fetch admin's condos to filter tickets
  useQuery({
    queryKey: ["adminCondos"],
    queryFn: async () => {
      const res = await getAllCondos({ page: 1, limit: 100 });
      if (res?.status === 'success' && res?.data) {
        const condosList = res.data.condos || res.data || [];
        const condoIds = condosList.map((condo: any) => condo.id);
        setAdminCondoIds(condoIds);
        return condoIds;
      }
      return [];
    },
  });

  // Fetch all filtered tickets for accurate stats calculation
  const { isFetching: isStatsFetching } = useQuery({
    queryKey: ["reportStats", adminCondoIds],
    queryFn: async () => {
      if (adminCondoIds.length === 0) {
        setAllFilteredTickets([]);
        return [];
      }
      const res = await GetAllReports({ page: 1, limit: 1000, search: "" });
      if (res?.statusCode === 200 && res.data) {
        const filteredTickets = res.data.tickets.filter((req: any) => {
          const ticketCondoId = req.user?.residentProfile?.condo?.id;
          if (!ticketCondoId) return false;
          return adminCondoIds.includes(ticketCondoId);
        });
        
        const formattedForStats = filteredTickets.map((req: any) => ({
          status: req.status
            ? req.status.replace(/_/g, " ").charAt(0).toUpperCase() +
              req.status.replace(/_/g, " ").slice(1)
            : "N/A",
        }));
        setAllFilteredTickets(formattedForStats);
        return formattedForStats;
      }
      return [];
    },
    enabled: adminCondoIds.length > 0,
  });

  // Update labels when language changes
  useEffect(() => {
    setStatsData((prev: any[]) =>
      prev.map((widget) => {
        if (widget.id === 1) {
          return { ...widget, label: t("reports.totalReports") };
        }
        if (widget.id === 2) {
          return { ...widget, label: t("reports.openReports") };
        }
        if (widget.id === 3) {
          return { ...widget, label: t("reports.closedReports") };
        }
        return widget;
      })
    );
  }, [t]);

  // Calculate stats from filtered tickets
  useEffect(() => {
    const total = allFilteredTickets.length;
    const open = allFilteredTickets.filter(
      (ticket: any) =>
        ticket.status?.toLowerCase() === "pending" ||
        ticket.status?.toLowerCase() === "in_progress" ||
        ticket.status?.toLowerCase() === "in progress" ||
        ticket.status?.toLowerCase() === "open"
    ).length;
    const closed = allFilteredTickets.filter(
      (ticket: any) =>
        ticket.status?.toLowerCase() === "closed" ||
        ticket.status?.toLowerCase() === "completed"
    ).length;

    setStatsData((prev: any[]) =>
      prev.map((widget) => {
        if (widget.id === 1) {
          return { ...widget, label: t("reports.totalReports"), counter: total.toString() };
        }
        if (widget.id === 2) {
          return { ...widget, label: t("reports.openReports"), counter: open.toString() };
        }
        if (widget.id === 3) {
          return { ...widget, label: t("reports.closedReports"), counter: closed.toString() };
        }
        return widget;
      })
    );
  }, [allFilteredTickets, t]);

  const columns: Column<any>[] = [
    { key: "id", title: t("reports.reportId"), width: 120 },
    { key: "title", title: t("reports.title") },
    { key: "type", title: t("reports.type") },
    { key: "occurence", title: t("reports.occurrence") },
    { key: "severity", title: t("reports.severity") },
    {
      key: "submittedBy",
      title: t("reports.submittedBy"),
    },
    { key: "condo", title: t("reports.condo") },
    { key: "submittedAt", title: t("reports.submittedAt") },
    {
      key: "status",
      title: t("reports.status"),
      align: "center",
      render: (value) => (
        <Pills
          label={value}
          variant={
            value === "Completed" || value === "Closed"
              ? "success"
              : value === "Revoked" || value === "Used"
              ? "danger"
              : "warning"
          }
          size="sm"
        />
      ),
    },
    { key: "attachedFiles", title: t("reports.attachedFiles") },
    {
      key: "actions",
      title: "",
      align: "left",
      width: 80,
      render: (_value: any, row: any) => (
        <div
          style={{ position: "relative", display: "inline-block" }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="btn btn-link p-0"
            onClick={(e) => {
              e.stopPropagation();
              setOpenMenuId(openMenuId === row.id ? null : row.id);
            }}
            aria-label="actions"
          >
            <span style={{ fontSize: 18, lineHeight: 1 }}>⋮</span>
          </button>

          {openMenuId === row.id && (
            <ul
              className="cg-action-dropdown"
              style={{
                position: "absolute",
                right: 0,
                top: "110%",
                zIndex: 1200,
                minWidth: 180,
                background: "#fff",
                boxShadow: "0 6px 12px rgba(0,0,0,0.12)",
                borderRadius: 6,
                padding: 0,
                margin: 0,
                listStyle: "none",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <li
                style={{ padding: "10px 14px", cursor: "pointer" }}
                onClick={() => {
                  setOpenMenuId(null);
                  setIsOpen(true);
                  setSelectedRow(row);
                }}
              >
                {t("reports.viewReportDetails")}
              </li>
              {row?.status !== "Closed" && row?.status !== "Completed" && (
                <li
                  style={{ padding: "10px 14px", cursor: "pointer" }}
                  onClick={() => {
                    setOpenMenuId(null);
                    markAsClosedMutation.mutate(row.backendId);
                  }}
                >
                  {t("reports.markAsClosed")}
                </li>
              )}
            </ul>
          )}
        </div>
      ),
    },
  ];

  const markAsClosedMutation = useMutation({
    mutationKey: ["mark-issue-closed"],
    mutationFn: async (issueId: string) => {
      const response = await UpdateReportStatus(issueId, "closed");
      if (response?.statusCode !== 200) {
        throw new Error(
          response?.message ||
            t("reports.failedToMarkAsClosed")
        );
      }
      return response;
    },
    onSuccess: () => {
      toast.success(t("reports.issueMarkedAsClosed"));
      getReportsMutation.mutate({ page, limit, search });
    },
    onError: (error: any) => {
      toast.error(
        error?.message || t("reports.failedToMarkAsClosed")
      );
    },
  });

  const getReportsMutation = useMutation({
    mutationKey: ["guestRequests"],
    mutationFn: async ({
      page,
      limit,
      search,
    }: {
      page: number;
      limit: number;
      search?: string;
    }) => {
      const res = await GetAllReports({ page, limit, search });
      if (res?.statusCode === 200 && res.data) {
        // Filter tickets to only show those related to admin's condos
        const filteredTickets = res.data.tickets.filter((req: any) => {
          const ticketCondoId = req.user?.residentProfile?.condo?.id;
          // If admin has no condos, don't show any tickets
          if (adminCondoIds.length === 0) return false;
          // Only show tickets where the user has a condo and it belongs to this admin
          if (!ticketCondoId) return false;
          return adminCondoIds.includes(ticketCondoId);
        });

        const formattedData = filteredTickets.map((req: any) => ({
          id: req.id?.slice(req.id.length - 6).toUpperCase() || "N/A",
          backendId: req.id,
          title: req.title || "N/A",
          type: req.category || "N/A",
          occurence: req.issueDate
            ? new Date(req.issueDate).toLocaleDateString("en-GB", {
                day: "numeric",
                month: "long",
                year: "numeric",
              }) +
              " -" +
              req.issueTime
            : "N/A",
          severity: req.priority
            ? req?.priority?.charAt(0).toUpperCase() + req.priority.slice(1)
            : "N/A",
          submittedBy: [req.user?.firstName, req.user?.lastName]
            .filter(Boolean)
            .join(" "),
          condo: req.user?.residentProfile?.unit,
          condoId: req.user?.residentProfile?.condo?.id, // Store condo ID for filtering
          req: new Date(req.createdAt).toLocaleString(),
          submittedAt: req.createdAt ? formatDateTime(req.createdAt) : "N/A",
          attachedFiles: req.attachments?.length || 0,
          status: req.status
            ? req.status.replace(/_/g, " ").charAt(0).toUpperCase() +
              req.status.replace(/_/g, " ").slice(1)
            : "N/A",
          attachments: req.attachments || [],
          description: req.description || "N/A",
          staff: req.staff || null,
        }));
        setAccessData(formattedData);

        // Update pagination based on filtered data
        const filteredTotal = formattedData.length;
        setPaginationData({
          total: filteredTotal,
          totalPages: Math.ceil(filteredTotal / limit),
        });
      }
      return res;
    },
  });

  useEffect(() => {
    // Only fetch reports after we have the admin's condo IDs
    if (adminCondoIds.length > 0 || adminCondoIds.length === 0) {
      getReportsMutation.mutate({ page, limit, search });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, search, adminCondoIds]);

  const handlePageChange = (pageIndex: number, pageSize: number) => {
    // page number clicked
    setPage(pageIndex + 1);

    // ONLY update limit if it actually changed
    if (pageSize !== limit) {
      setLimit(pageSize);
      setPage(1); // reset page ONLY when page size changes
    }
  };

  const handleSearch = (v: string) => {
    if (v !== lastSearch) {
      setSearch(v);
      setLastSearch(v);
      setPage(1); // Only reset when search changes
    }
  };

  useEffect(() => {
    const onDocClick = () => setOpenMenuId(null);
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("reports.title")}
            pageTitles={[
              { label: t("accessRequests.dashboard"), path: "/" },
              { label: t("reports.title"), path: "/reports" },
            ]}
          />
          <Row>{isStatsFetching ? <Spinner /> : <Widgets data={statsData} />}</Row>
          <Row className="align-items-center justify-content-between">
            <Col xs={12} md={8} lg={8} className="p-0">
              <Heading
                title={t("reports.manageReportedIssues")}
                description={t("reports.trackAndResolve")}
                button={false}
              />
            </Col>
            <Col xs="auto" className="d-flex justify-content-end">
              <Filter
                placeholder={t("reports.searchIssues")}
                value={search}
                onSearch={handleSearch}
                onClear={() => {
                  setSearch("");
                  setLastSearch("");
                  setPage(1);
                }}
              />
            </Col>
          </Row>

          {getReportsMutation.isError ? (
            <div className="text-center my-5">
              <div className="text-danger mb-3">
                <i
                  className="ri-error-warning-line"
                  style={{ fontSize: "3rem" }}
                />
              </div>
              <h5 className="text-danger">{t("reports.failedToLoadReports")}</h5>
              <p className="text-muted">
                {(getReportsMutation.error as any)?.message ||
                  t("reports.pleaseTryAgain")}
              </p>
              <Button
                className="btn btn-secondary"
                onClick={() =>
                  getReportsMutation.mutate({ page, limit, search })
                }
              >
                {t("reports.retry")}
              </Button>
            </div>
          ) : (
            <Table
              columns={columns}
              data={accessData}
              pagination={{
                pageIndex: page - 1,
                pageSize: limit,
                total: paginationData.total,
              }}
              onPageChange={handlePageChange}
              loading={getReportsMutation.isPending}
              selectable
              onSelectionChange={(sel) => console.log("selected", sel)}
            />
          )}
        </Container>
      </div>
      <ViewReportDetailsModal
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
          setSelectedRow(null);
        }}
        issue={selectedRow}
        refetchRequests={() => {
          getReportsMutation.mutate({ page, limit, search });
        }}
      />
    </React.Fragment>
  );
};
export default Reports;
